import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export async function renderPdfFirstPage(file: File): Promise<Blob> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const loadingTask = getDocument({ data: bytes });
  const pdfDocument = await loadingTask.promise;
  try {
    if (pdfDocument.numPages < 1) throw new Error('PDF ไม่มีหน้าเอกสาร');
    const page = await pdfDocument.getPage(1);
    const initial = page.getViewport({ scale: 1 });
    const scale = Math.min(2.5, 2_400 / Math.max(initial.width, initial.height));
    const viewport = page.getViewport({ scale });
    const canvas = window.document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('อุปกรณ์ไม่รองรับการแปลง PDF');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, viewport, canvas }).promise;
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/webp', 0.9),
    );
    if (!blob) throw new Error('แปลงหน้าแรกของ PDF ไม่สำเร็จ');
    return blob;
  } finally {
    await loadingTask.destroy();
  }
}

export interface PdfRenderProgress {
  pageNumber: number;
  totalPages: number;
  sourcePages: number;
  blob: Blob;
}

export async function renderPdfPages(
  file: File,
  onPage: (page: PdfRenderProgress) => Promise<void>,
  maximumPages = 200,
): Promise<{ totalPages: number; sourcePages: number; truncated: boolean }> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const loadingTask = getDocument({ data: bytes });
  const pdfDocument = await loadingTask.promise;
  const totalPages = Math.min(pdfDocument.numPages, maximumPages);
  try {
    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
      const page = await pdfDocument.getPage(pageNumber);
      const initial = page.getViewport({ scale: 1 });
      // Keep Thai glyphs sharp while bounding the payload sent through the
      // Worker. Large lossless PNG pages expand again when encoded as Base64
      // and can exceed the memory/time budget before reaching Google Vision.
      const scale = Math.min(2.5, 2_100 / Math.max(initial.width, initial.height));
      const viewport = page.getViewport({ scale });
      const canvas = window.document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) throw new Error('อุปกรณ์ไม่รองรับการแปลง PDF');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: context, viewport, canvas }).promise;
      let blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
      if (blob && blob.size > 4.5 * 1024 * 1024) {
        blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
      }
      if (!blob) throw new Error(`แปลง PDF หน้า ${pageNumber} ไม่สำเร็จ`);
      await onPage({ pageNumber, totalPages, sourcePages: pdfDocument.numPages, blob });
      page.cleanup();
    }
    return {
      totalPages,
      sourcePages: pdfDocument.numPages,
      truncated: pdfDocument.numPages > maximumPages,
    };
  } finally {
    await loadingTask.destroy();
  }
}
